import apiSlice from '..';

export type ReportOfSaleVersion = {
  id: number;
  version: string;
  url: string;
  is_active: boolean;
  actor: string;
  action: 'Created' | 'Modified';
  created_at: string;
};

export type ReportOfSaleVersions = {
  admin?: ReportOfSaleVersion[];
  buyer?: ReportOfSaleVersion[];
  seller?: ReportOfSaleVersion[];
};

export type RosReportType = 'admin' | 'buyer' | 'seller';

export type RosVehicle = {
  vin: string;
  year: string;
  model: string;
  make: string;
  mileage: string;
  price: string;
  selling_date: string;
  license_plate?: string;
  totalWithoutFee?: string;
};

export type RosParty = {
  dealership_name: string;
  number: string;
  seller_permit_number?: string;
  name: string;
  address: string;
  phone_number: string;
  mobile_number?: string;
  city: string;
  state: string;
  zip_code: string;
  fee?: string;
  additional_fees_total?: string;
};

export type RosEditData = {
  auction_id: string;
  pickup_instruction?: string;
  vehicle: RosVehicle;
  seller: RosParty;
  buyer: RosParty;
  is_buyer_report?: boolean;
  buyer_fee?: string;
  processing_fee?: string;
  transportation_fee?: string;
  additional_fees_total?: string;
  vehicle_with_buy_fee?: string;
};

export type SaveCustomReportResult = {
  id: number;
  version: string;
  url: string;
  created_at: string;
};

export const reportOfSaleApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getReportOfSaleVersions: builder.query<ReportOfSaleVersions, { vehicleId: number }>({
      query: ({ vehicleId }) => ({
        url: `vehicles/${vehicleId}/report-of-sale/versions`,
      }),
      transformResponse: (res: { data: ReportOfSaleVersions }) => res.data,
      providesTags: ['ReportOfSaleVersions'],
    }),
    getReportOfSaleEditData: builder.query<
      RosEditData,
      { vehicleId: number; reportType: RosReportType; versionId?: number }
    >({
      query: ({ vehicleId, reportType, versionId }) => ({
        url: `vehicles/${vehicleId}/report-of-sale/${reportType}/edit-data`,
        ...(versionId != null ? { params: { version_id: versionId } } : {}),
      }),
      transformResponse: (res: { data: RosEditData }) => res.data,
    }),
    saveCustomReportOfSale: builder.mutation<
      SaveCustomReportResult,
      { vehicleId: number; reportType: RosReportType; payload: RosEditData }
    >({
      query: ({ vehicleId, reportType, payload }) => ({
        method: 'POST',
        url: `vehicles/${vehicleId}/report-of-sale/${reportType}/custom`,
        body: payload,
      }),
      transformResponse: (res: { data: SaveCustomReportResult }) => res.data,
      invalidatesTags: ['ReportOfSaleVersions'],
    }),
    sendReportOfSale: builder.mutation<
      void,
      { vehicleId: number; reportType: RosReportType; emails: string[]; notes: string; versionId: number }
    >({
      query: ({ vehicleId, reportType, emails, notes, versionId }) => ({
        method: 'POST',
        url: `vehicles/${vehicleId}/report-of-sale/${reportType}/send`,
        body: { emails, notes, id: versionId },
      }),
      invalidatesTags: ['ReportOfSaleVersions'],
    }),
    downloadReportOfSale: builder.mutation<
      string,
      { vehicleId: number; reportType: RosReportType; versionId: number }
    >({
      query: ({ vehicleId, reportType, versionId }) => ({
        method: 'POST',
        url: `vehicles/${vehicleId}/report-of-sale/${reportType}/download`,
        body: { id: versionId },
      }),
      transformResponse: (res: { data: { url: string } }) => res.data.url,
    }),
    setActiveReportOfSaleVersion: builder.mutation<
      ReportOfSaleVersion,
      { vehicleId: number; reportType: RosReportType; id: number }
    >({
      query: ({ vehicleId, reportType, id }) => ({
        method: 'POST',
        url: `vehicles/${vehicleId}/report-of-sale/${reportType}/active`,
        body: { id },
      }),
      transformResponse: (res: { data: ReportOfSaleVersion }) => res.data,
      // Only one version per report type is active — flip the clicked row on and
      // the rest of that type off optimistically, rolling back if the call fails.
      async onQueryStarted({ vehicleId, reportType, id }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          reportOfSaleApiSlice.util.updateQueryData('getReportOfSaleVersions', { vehicleId }, draft => {
            const list = draft[reportType];
            if (list) list.forEach(v => (v.is_active = v.id === id));
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetReportOfSaleVersionsQuery,
  useGetReportOfSaleEditDataQuery,
  useSaveCustomReportOfSaleMutation,
  useSendReportOfSaleMutation,
  useDownloadReportOfSaleMutation,
  useSetActiveReportOfSaleVersionMutation,
} = reportOfSaleApiSlice;
